import Avatar from 'assets/profileAvatars/Avatar.png';
import { Button, Spinner, StatusBlock } from 'view/components';
import {
  FavoriteIcon,
  PortfolioIcon,
  UserStarIcon,
  LocationPinIcon,
  BriefCaseGreenIcon,
} from 'assets';
import CircularProgress from '@mui/material/CircularProgress';
import { getMatchScoreColor } from 'view/pages/profile/components/JobPostsListItem';
import { RolesEnum } from 'infrastructure/enums/RolesEnum';
import { useMadeFavoriteById } from 'view/pages/candidates/hooks/useMadeFavoriteById';
import * as S from './styled';

const InfoItem = ({ backgroundColor, icon, title, desc }) => {
  return (
    <S.InfoItem style={{ backgroundColor }}>
      {icon}
      <div>
        <S.InfoItemTitle>{title}</S.InfoItemTitle>
        <S.InfoItemDesc>{desc}</S.InfoItemDesc>
      </div>
    </S.InfoItem>
  );
};

const CandidateProfileItem = ({
  candidate,
  roleName,
  getJobSeekerProfileById,
  jobPostId = null,
}) => {
  /* const matches = useMediaQuery(theme.breakpoints.down('desktop')); */
  const params = new URLSearchParams(window.location.search);
  const matchScore = params.get('matchScore');

  const { sendFavoriteInfo, isLoading } = useMadeFavoriteById();

  const convertToYearsMonths = (value: string) => {
    if (!value) return '';
    const parts = value?.split('.');

    const years = parseInt(parts[0], 10);
    const months = parts[1] ? parseInt(parts[1], 10) : 0;

    return `${years} years ${months} month`;
  };

  const getJobLocationDescription = (jobLocation: any) => {
    if (!jobLocation) return '';
    const locations: Array<string> = [];

    if (jobLocation.onSite) locations.push('On Site');
    if (jobLocation.remote) locations.push('Remote');
    if (jobLocation.hybrid) locations.push('Hybrid');

    return locations?.length > 0 ? `(${locations.join(', ')})` : '';
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const getCurrentRoleTitle = (candidate: any) => {
    if (!candidate?.workExperience?.length) return 'No professional experience listed yet';

    const lastRole = candidate.workExperience.reduce((latest, current) => {
      const monthIndexCurrent = monthNames.indexOf(current.startDateMonth);
      const monthIndexLatest = monthNames.indexOf(latest.startDateMonth);

      // Сравниваем текущий опыт с последним по месяцу и году
      if (
        current.startDateYear > latest.startDateYear ||
        (current.startDateYear === latest.startDateYear && monthIndexCurrent > monthIndexLatest)
      ) {
        return current;
      }

      return latest;
    }, candidate.workExperience[0]); // Начинаем с первого элемента

    return lastRole
      ? `${lastRole.jobTitle} at ${lastRole.companyName}`
      : 'No professional experience listed yet';
  };

  const getCurrentRoleDesc = (candidate: any) => {
    if (!candidate?.workExperience?.length) return 'No Current Role';

    const currentRoles = candidate.workExperience.filter((role) => {
      return role.isCurrentCompany;
    });

    if (currentRoles.length === 0) {
      // Если нет текущих ролей, показываем последний опыт
      const lastRole = candidate.workExperience.reduce((latest, current) => {
        const monthIndexCurrent = monthNames.indexOf(current.startDateMonth);
        const monthIndexLatest = monthNames.indexOf(latest.startDateMonth);

        // Сравниваем текущий опыт с последним по месяцу и году
        if (
          current.startDateYear > latest.startDateYear ||
          (current.startDateYear === latest.startDateYear && monthIndexCurrent > monthIndexLatest)
        ) {
          return current;
        }

        return latest;
      }, candidate.workExperience[0]); // Начинаем с первого элемента

      return `Last held role (ended ${lastRole.endDateMonth} ${lastRole.endDateYear})`;
    }

    if (currentRoles.length === 1) {
      // Если одна текущая роль
      return `Current Role at ${currentRoles[0].companyName}`;
    }

    // Если несколько текущих ролей
    return 'Currently holds multiple roles';
  };

  return (
    <S.CandidateListItem>
      <S.CandidateListItemHeading>
        <S.CandidateListItemHeadingInfo>
          <div style={{ width: '72px', height: '72px', position: 'relative' }}>
            <img
              src={candidate?.photo?.link || Avatar}
              alt="avatar"
              style={{ width: '72px', height: '72px' }}
            />

            {Number(matchScore) || Number(candidate?.matchScore) ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <CircularProgress
                  variant="determinate"
                  value={Number(matchScore) || Number(candidate?.matchScore)}
                  style={{
                    width: '72px',
                    height: '72px',
                    color: getMatchScoreColor(Number(matchScore) || Number(candidate?.matchScore)),
                  }}
                />
              </div>
            ) : null}
          </div>

          <S.CandidateListItemHeadingInfoTexts>
            <S.CandidateListItemHeadingInfoTitle>
              {candidate.jobTitle || 'No Job Title'} <span>{candidate.username}</span>
            </S.CandidateListItemHeadingInfoTitle>
            <S.CandidateListItemHeadingInfoSalary>
              {!candidate.salaryMin && !candidate.salaryMax
                ? 'Salary not specified'
                : `$${candidate.salaryMin.toLocaleString()} - $${candidate.salaryMax.toLocaleString()}`}
            </S.CandidateListItemHeadingInfoSalary>
            {Number(matchScore) || Number(candidate?.matchScore) ? (
              <S.CandidateListItemHeadingInfoSalary>
                {Number(matchScore) || Number(candidate?.matchScore)}% match
              </S.CandidateListItemHeadingInfoSalary>
            ) : null}
          </S.CandidateListItemHeadingInfoTexts>
        </S.CandidateListItemHeadingInfo>
        {/* {!matches && ( */}
        <S.CandidateListItemHeadingButtons>
          <div>
            {candidate?.applicationStatus ? (
              <StatusBlock appStatus={candidate?.applicationStatus?.statusForRecruiter} />
            ) : null}
            {roleName === RolesEnum.JOB_SEEKER ? null : (
              <Button
                variant="outlined"
                text=""
                endIcon={
                  isLoading ? (
                    <Spinner variant="outlined" />
                  ) : (
                    <FavoriteIcon style={{ fill: candidate.isFavourited ? '#663497' : '#fff' }} />
                  )
                }
                onClick={() => {
                  sendFavoriteInfo(
                    candidate.id,
                    () => {
                      if (getJobSeekerProfileById)
                        getJobSeekerProfileById(candidate.id, jobPostId || null);
                    },
                    candidate.isFavourited
                  );
                }}
                style={{
                  padding: '10px',
                  width: '40px',
                  minWidth: '40px',
                  height: '40px',
                  minHeight: '40px',
                }}
              />
            )}
          </div>
          <p>
            Updated on{' '}
            {new Date(candidate.updatedAt).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </S.CandidateListItemHeadingButtons>
        {/* )} */}
      </S.CandidateListItemHeading>
      {/* <S.Separator /> */}
      <S.CandidateListItemMain>
        <InfoItem
          backgroundColor="#F5EFFA"
          icon={<PortfolioIcon />}
          title={getCurrentRoleTitle(candidate)} // at ${candidate?.jobTitle}
          desc={getCurrentRoleDesc(candidate)}
        />
        <InfoItem
          backgroundColor="#DDF6FF"
          icon={<UserStarIcon />}
          title={
            candidate.totalExperienceYears === '0.0'
              ? 'Experience not provided'
              : convertToYearsMonths(candidate.totalExperienceYears)
          }
          desc="Total Years of Experience"
        />
        <InfoItem
          backgroundColor="#FFE7D4"
          icon={<LocationPinIcon />}
          title={`${candidate?.location?.name || 'Location not provided'} ${getJobLocationDescription(candidate?.jobLocation)}`}
          desc="Location"
        />
        <InfoItem
          backgroundColor="#F3F9EE"
          icon={<BriefCaseGreenIcon />}
          title={`${
            candidate?.languages?.length > 0
              ? candidate?.languages
                  ?.map((el) => {
                    return el.name;
                  })
                  .join(', ')
              : 'Languages not provided'
          }`}
          desc="Languages"
        />
      </S.CandidateListItemMain>
    </S.CandidateListItem>
  );
};

export default CandidateProfileItem;
